import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase } from '../services/supabase'
import { createLogger } from '../utils/logger'
import { clearSessionExpired, redirectToLogin } from '../utils/sessionAuth'
import { setSentryUserContext } from '../utils/sentryTracking'
import { finalizePendingAccessCode } from '../utils/accessCode'
import { initPostHog, identifyUser, deidentifyUser } from '../services/posthog'

const logger = createLogger('auth')

export const MAP_READY_SESSION_KEY = 'ls_map_ready'

const AuthContext = createContext()

export function AuthLoadingScreen({ overlay = false }) {
  return (
    <div
      className={`flex min-h-screen items-center justify-center bg-white${overlay ? ' fixed inset-0 z-[10000]' : ''}`}
    >
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-900"
        role="status"
        aria-label="Loading"
      />
    </div>
  )
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [authReady, setAuthReady] = useState(false)
  const [authBusy, setAuthBusy] = useState(true)
  const userRef = useRef(null)

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    initPostHog()
  }, [])

  useEffect(() => {
    const finishAuth = () => {
      setAuthReady(true)
      setAuthBusy(false)
    }

    const getSessionAndProfile = async (session, { blocking = false } = {}) => {
      if (blocking && !userRef.current) {
        setAuthBusy(true)
      }
      try {
      if (session?.user) {
        clearSessionExpired()
        // Link pending access code before reading profile — RLS requires
        // access_code_id (or admin) to SELECT public.users.
        await finalizePendingAccessCode()
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('is_admin')
          .eq('id', session.user.id)
          .single()
        if (profileError || !profile) {
          // No linked access code (and not admin) — auth account exists but app is gated.
          logger.warn('App access denied — no linked access code', {
            user_id: session.user.id,
            action: 'access_code_gate',
          }, profileError)
          await supabase.auth.signOut()
          setUser(null)
          setIsAdmin(false)
          setSentryUserContext(null)
          deidentifyUser()
          try {
            sessionStorage.setItem(
              'ls_auth_flash',
              'A valid access code is required to use LandSentry. If you already signed up with a code, contact support.'
            )
          } catch {
            /* ignore */
          }
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login'
          }
          return
        }
        setUser(session.user)
        setSentryUserContext(session.user)
        identifyUser(session.user.id, { email: session.user.email })
        setIsAdmin(Boolean(profile.is_admin))
        logger.info('User authenticated', {
          user_id: session.user.id,
          auth_method: 'email',
          view: 'app',
        })
      } else {
        setUser(null)
        setIsAdmin(false)
        setSentryUserContext(null)
        deidentifyUser()
      }
      } catch (err) {
        logger.error('Failed to resolve auth session', {
          action: 'resolve_session',
        }, err)
        setUser(null)
        setIsAdmin(false)
        setSentryUserContext(null)
        deidentifyUser()
      } finally {
      finishAuth()
      }
    }

    // Initial load
    supabase.auth.getSession()
      .then(({ data: { session } }) => getSessionAndProfile(session))
      .catch((err) => {
        logger.error('Failed to get auth session', { action: 'get_session' }, err)
        finishAuth()
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        return
      }

      if (event === 'SIGNED_OUT') {
        setUser(null)
        setIsAdmin(false)
        setSentryUserContext(null)
        deidentifyUser()
        try {
          sessionStorage.removeItem(MAP_READY_SESSION_KEY)
        } catch {
          /* ignore */
        }
        finishAuth()
        redirectToLogin()
        return
      }

      if (event === 'TOKEN_REFRESHED' && session?.user) {
        clearSessionExpired()
        logger.debug('Auth token refreshed', { user_id: session.user.id, action: 'token_refreshed' })
        return
      }

      const isFreshSignIn = event === 'SIGNED_IN' && !userRef.current
      getSessionAndProfile(session, { blocking: isFreshSignIn })
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  const login = async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password })
  }

  const signUp = async (email, password) => {
    return await supabase.auth.signUp({ email, password })
  }

  const signInWithOAuth = async (provider) => {
    // Preserve the current origin (localhost / Vercel preview / production) so OAuth
    // does not bounce to the Supabase Site URL (usually production landsentry.com).
    const redirectTo = `${window.location.origin}/login`
    const options = { redirectTo }

    if (provider === 'azure') {
      return await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          ...options,
          scopes: 'email profile openid',
        },
      })
    }
    return await supabase.auth.signInWithOAuth({ provider, options })
  }

  const logout = async () => {
    await supabase.auth.signOut()
  }

  const refreshUser = async () => {
    try {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) {
      setUser(session.user)
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', session.user.id)
        .single()
      if (profileError) {
        logger.warn('Failed to refresh user profile', {
          user_id: session.user.id,
          action: 'refresh_user_profile',
        }, profileError)
      }
      setIsAdmin(profile?.is_admin || false)
    } else {
      setUser(null)
      setIsAdmin(false)
    }
    } catch (err) {
      logger.error('Failed to refresh user', { action: 'refresh_user' }, err)
    }
  }
  
  const value = {
    user,
    isAdmin,
    login,
    signUp,
    signInWithOAuth,
    logout,
    refreshUser,
    loading: !authReady,
  }

  if (!authReady) {
    return (
      <AuthContext.Provider value={value}>
        <AuthLoadingScreen />
      </AuthContext.Provider>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {authBusy && <AuthLoadingScreen overlay />}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 